import { Directive, ViewChild } from '@angular/core';
import { marker } from '@common/translate-marker';
import {
    GENERAL_ACT_STATUS_PILL_OVERRIDES,
    getStatusPillClass,
    PCS_DOCUMENT_STATUS_PILL_OVERRIDES,
    RELEASE_ORDER_STATUS_PILL_OVERRIDES,
    SHIPMENT_POOL_STATUS_PILL_OVERRIDES,
    StatusPillOverrides,
    STORAGE_DOCUMENT_ITEM_STATUS_PILL_OVERRIDES,
    VESSEL_VISIT_STATUS_PILL_OVERRIDES,
    VISIT_STATUS_PILL_OVERRIDES
} from '@common/utils/status-pill.util';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CustomIcons } from '../constants';
import { UserModel } from '../models/User.model';
import { ModalService } from '../services/modal.service';
import { currentUserSubject } from '../services/user.service';

@Directive()
export class CommonController {
    user: UserModel;
    currentUser$: Subscription;
    protected initialSelectedTabIndex: number;
    readonly CustomIcons = CustomIcons;

    @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

    constructor(
        protected dialogService: ModalService,
        protected translateService: TranslateService
    ) {
        this.user = currentUserSubject.getValue();
    }

    hasPermission(fullPermissionName: string) {
        const user = currentUserSubject.getValue();
        if (!user) {
            return false;
        }
        return user.hasPermission(fullPermissionName);
    }

    belongsToOrganization(organization: any): boolean {
        const user = currentUserSubject.getValue();
        if (!user || !organization) {
            return false;
        }
        return user.belongsToOrganization(organization);
    }

    userHasRole(roleName: string) {
        const user = currentUserSubject.getValue();
        if (!user) {
            return false;
        }
        return user.hasRole(roleName);
    }

    userBelongsToOrganization(organizations: any) {
        const user = currentUserSubject.getValue();
        if (!user) {
            return false;
        }
        return user.organization.id.toString() === organizations.toString();
    }

    triggerDeactivationMessage(): Observable<any> {
        return this.dialogService
            .confirm(
                this.translateService.instant(marker('Confirm removal')) as string,
                this.translateService.instant(marker('Are you sure you want to remove this item?')) as string,
                this.translateService.instant(marker('YES')) as string,
                this.translateService.instant(marker('No')) as string
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)));
    }

    triggerSaveMessage(message = null): Observable<any> {
        return this.dialogService
            .confirm(
                this.translateService.instant(marker('Confirm save')) as string,
                this.translateService.instant(
                    marker(message ? message : 'Are you sure you want to save this item?')
                ) as string,
                this.translateService.instant(marker('YES')) as string,
                this.translateService.instant(marker('No')) as string
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)));
    }

    processDates(model: any, datePropsOnModel: any[]) {
        if (!model) return;

        for (const [key, value] of Object.entries(model)) {
            if (datePropsOnModel.includes(key, 0) && value !== null) {
                model[key] = new Date(value as string);
            }
        }
    }

    /** Displays tooltips in kendo grid columns where text is longer than a given cell width */
    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (
            (element.nodeName === 'TD' || element.nodeName === 'TH' || element.nodeName === 'SPAN') &&
            element.offsetWidth < element.scrollWidth
        ) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    rowCallback = (context: RowClassArgs) => {
        const { dataItem } = context;

        if (dataItem?.customsHold) {
            const color = dataItem.isCustomsHoldVisible ? dataItem?.guiColor || 'orange' : false;
            return {
                [color]: true
            };
        }

        if (
            (typeof dataItem?.numberOfUnits === 'number' && dataItem?.numberOfUnits < 0) ||
            (typeof dataItem?.nettWeight === 'number' && dataItem?.nettWeight < 0) ||
            (typeof dataItem?.grossWeight === 'number' && dataItem?.grossWeight < 0) ||
            (typeof dataItem?.volume === 'number' && dataItem?.volume < 0) ||
            (typeof dataItem?.onStockNumberOfUnits === 'number' && dataItem?.onStockNumberOfUnits < 0) ||
            (typeof dataItem?.onStockNettWeight === 'number' && dataItem?.onStockNettWeight < 0) ||
            (typeof dataItem?.onStockGrossWeight === 'number' && dataItem?.onStockGrossWeight < 0) ||
            (typeof dataItem?.onStockVolume === 'number' && dataItem?.onStockVolume < 0)
        ) {
            return {
                ['red']: true
            };
        }
    };

    private getMoveStatusData(item: any) {
        return {
            moveCanceled: item?.moveCanceled,
            moveDeleted: item?.moveDeleted ?? item?.deletedDate,
            movePerformed: item?.movePerformed ?? item?.containerMoveDatePerformed ?? item?.datePerformed
        };
    }

    private getActiveMoveStatus(item: any): 'canceled' | 'deleted' | 'performed' | 'none' {
        if (!item) {
            return 'none';
        }

        const status = this.getMoveStatusData(item);

        // A concrete move keeps its business-effective DatePerformed for history.
        // Its explicit outcome therefore determines the displayed state, even when
        // DatePerformed is later than the audit timestamp for unconfirming/stopping it.
        if (Object.prototype.hasOwnProperty.call(item, 'datePerformed')) {
            if (status.moveDeleted) {
                return 'deleted';
            }

            if (status.moveCanceled) {
                return 'canceled';
            }

            return status.movePerformed ? 'performed' : 'none';
        }

        const transitions = [
            { type: 'canceled', time: status.moveCanceled ? new Date(status.moveCanceled).getTime() : 0 },
            { type: 'deleted', time: status.moveDeleted ? new Date(status.moveDeleted).getTime() : 0 },
            { type: 'performed', time: status.movePerformed ? new Date(status.movePerformed).getTime() : 0 }
        ];

        const validTransitions = transitions.filter((t) => t.time && t.time > 0);

        if (validTransitions.length === 0) {
            return 'none';
        }

        validTransitions.sort((a, b) => b.time - a.time);

        return validTransitions[0].type as 'canceled' | 'deleted' | 'performed';
    }

    getMoveStatusClass(item: any, showPerformed: boolean = true): string {
        if (!item) {
            return '';
        }

        const activeStatus = this.getActiveMoveStatus(item);

        if (activeStatus === 'canceled') {
            return 'container-number-dot-canceled';
        }

        if (activeStatus === 'deleted') {
            return 'container-number-dot-unconfirmed';
        }

        if (showPerformed && activeStatus === 'performed') {
            return 'container-number-dot-realized';
        }

        return '';
    }

    getMoveStatusTooltip(item: any, showPerformed: boolean = true): string {
        if (!item) {
            return '';
        }

        const activeStatus = this.getActiveMoveStatus(item);

        if (activeStatus === 'canceled') {
            return this.translateService.instant(marker('Stopped Move')) as string;
        }
        if (activeStatus === 'deleted') {
            return this.translateService.instant(marker('Unconfirmed Move')) as string;
        }
        if (showPerformed && activeStatus === 'performed') {
            return this.translateService.instant(marker('Confirmed Move')) as string;
        }

        return '';
    }

    getMOTClass(mot: string): string {
        if (!mot) {
            return '';
        } else if (mot.toLowerCase() === 'truck') {
            return 'fa-truck';
        } else if (mot.toLowerCase() === 'wagon' || mot.toLowerCase() === 'rail') {
            return 'fa-train';
        } else if (mot.toLowerCase() === 'vessel') {
            return 'fa-ship';
        } else if (mot.toLowerCase() === 'yard') {
            return 'fa-cubes';
        } else {
            return '';
        }
    }

    protected resolveStatusPillClass(statusId: string | null | undefined, overrides?: StatusPillOverrides): string {
        return getStatusPillClass(statusId, overrides);
    }

    protected resolveVisitStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, VISIT_STATUS_PILL_OVERRIDES);
    }

    protected resolveShipmentPoolStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, SHIPMENT_POOL_STATUS_PILL_OVERRIDES);
    }

    protected resolveReleaseOrderStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, RELEASE_ORDER_STATUS_PILL_OVERRIDES);
    }

    protected resolveStorageDocumentItemStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, STORAGE_DOCUMENT_ITEM_STATUS_PILL_OVERRIDES);
    }

    protected resolveGeneralActStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, GENERAL_ACT_STATUS_PILL_OVERRIDES);
    }

    protected resolvePcsDocumentStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, PCS_DOCUMENT_STATUS_PILL_OVERRIDES);
    }

    protected resolveVesselVisitStatusPillClass(statusId: string | null | undefined): string {
        return this.resolveStatusPillClass(statusId, VESSEL_VISIT_STATUS_PILL_OVERRIDES);
    }

    getGeneralActStatusClass(statusId: string | null | undefined): string {
        return this.resolveGeneralActStatusPillClass(statusId);
    }

    getPcsDocumentStatusClass(statusId: string | null | undefined): string {
        return this.resolvePcsDocumentStatusPillClass(statusId);
    }

    normalizeCase(text: string): string {
        if (!text) return text;
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }
}
